import { c as _c } from "react/compiler-runtime";
import type { Color } from '@onefootprint/design-tokens';
import { useIntl } from '@onefootprint/hooks';
import { IcoBuilding16, IcoGlobe16, IcoHome16, IcoUser16 } from '@onefootprint/icons';
import type { Entity } from '@onefootprint/request-types/dashboard';
import { Card, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CardTitle } from '@/components/entity-onboarding-details';
import type { DeviceInsightEvent, DeviceVisitEvent } from '@/entities/components/details/hooks/use-entity-device-insights';
import { isDeviceVisit } from '@/entities/components/details/hooks/use-entity-device-insights';
import useSession from '@/hooks/use-session';
import { displayForUserAgent, icoForUserAgent } from '@/utils/user-agent';
import MapComponent from '../map';
import MapMarker from '../map/components/map-marker';
import AddressRow from './components/address-row';
import AddressType from './components/address-row/types';
import DeviceRow from './components/device-row';
import FingerprintAnnouncement from './components/fingerprint-announcement';
import VisitRow from './components/visit-row';
import useEntries from './hooks/use-entries';
import useScrollFade from './hooks/use-scroll-fade';
import getScrollBehavior from './utils/get-scroll-behavior';
type Pin = {
  id: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  label: string;
  getIcon: (color: Color) => ReactNode;
};
type ContentProps = {
  entity: Entity;
  events: (DeviceInsightEvent | DeviceVisitEvent)[];
};
type GroupProps = {
  title: string;
  count?: number;
  titleAside?: ReactNode;
  /** Scrolls its rows inside a capped height, which shrinks to a row and a half when the column runs out of room. */
  isScrollable?: boolean;
  /** Stays under the rows rather than scrolling with them. */
  footer?: ReactNode;
  children: ReactNode;
};
const Group = t0 => {
  const $ = _c(28);
  const {
    title,
    count,
    titleAside,
    isScrollable,
    footer,
    children
  } = t0;
  const boxRef = useRef(null);
  const contentRef = useRef(null);
  const {
    overflow,
    maskImage,
    onScroll
  } = useScrollFade(boxRef, contentRef);
  const t1 = isScrollable ? "flex max-h-120 min-h-36 flex-col" : "shrink-0";
  let t2;
  if ($[0] !== count || $[1] !== title || $[2] !== titleAside) {
    t2 = <CardTitle title={title} count={count} rightContent={titleAside} />;
    $[0] = count;
    $[1] = title;
    $[2] = titleAside;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== maskImage) {
    t3 = {
      maskImage,
      WebkitMaskImage: maskImage
    };
    $[4] = maskImage;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== children) {
    t4 = <div ref={contentRef}>{children}</div>;
    $[6] = children;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onScroll || $[9] !== t3 || $[10] !== t4) {
    t5 = <div ref={boxRef} className="min-h-0 overflow-y-auto" style={t3} onScroll={onScroll}>{t4}</div>;
    $[8] = onScroll;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== overflow.start) {
    t6 = overflow.start && <div aria-hidden={true} className="pointer-events-none absolute inset-x-0 top-0 h-3 rounded-t-default bg-gradient-to-b from-black/6 to-transparent" />;
    $[12] = overflow.start;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== footer || $[15] !== overflow.end) {
    t7 = overflow.end && <div aria-hidden={true} className={cx("pointer-events-none absolute inset-x-0 bottom-0 h-3 bg-gradient-to-t from-black/6 to-transparent", !footer && "rounded-b-default")} />;
    $[14] = footer;
    $[15] = overflow.end;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className="relative flex min-h-0 flex-col p-1">{t5}{t6}{t7}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== footer || $[22] !== t8) {
    t9 = <Card.Body className="flex min-h-0 flex-col">{t8}{footer}</Card.Body>;
    $[21] = footer;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t1 || $[25] !== t2 || $[26] !== t9) {
    t10 = <Card.Root className={t1}>{t2}{t9}</Card.Root>;
    $[24] = t1;
    $[25] = t2;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
};
const isSuperseded = (event: DeviceInsightEvent | DeviceVisitEvent) => event.event.kind === 'device_insight' && event.event.data.isSuperseded;
const Content = t0 => {
  const $0 = _c(85);
  const {
    entity,
    events
  } = t0;
  const {
    data: session
  } = useSession();
  let t1;
  if ($0[0] !== events) {
    t1 = events.some(_temp);
    $0[0] = events;
    $0[1] = t1;
  } else {
    t1 = $0[1];
  }
  const isFingerprint = t1;
  let t2;
  if ($0[2] !== events || $0[3] !== session.user?.isFirmEmployee) {
    t2 = session.user?.isFirmEmployee ? events : events.filter(_temp2);
    $0[2] = events;
    $0[3] = session.user?.isFirmEmployee;
    $0[4] = t2;
  } else {
    t2 = $0[4];
  }
  const visibleEvents = t2;
  let t3;
  if ($0[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "deviceInsights"
    };
    $0[5] = t3;
  } else {
    t3 = $0[5];
  }
  const {
    t
  } = useTranslation("entity-details", t3);
  const {
    devices,
    signIns,
    signInCount,
    isSignInsCollapsible,
    isShowingAllSignIns,
    toggleAllSignIns,
    shownEvents,
    visits,
    countryPins,
    lines,
    addresses,
    selectedId,
    selectedRowId,
    selectedFrom,
    selectedCoords,
    pickCount,
    select
  } = useEntries(entity, visibleEvents);
  const mapCardRef = useRef(null);
  const {
    formatDateWithTime
  } = useIntl();
  let t4;
  if ($0[6] !== formatDateWithTime || $0[7] !== t) {
    t4 = t5 => {
      const {
        event: event_1
      } = t5;
      return [t($ => $.insightKind[event_1.data.kind]), displayForUserAgent(event_1.data.insight.userAgent ?? "").replace(/^-$/, ""), formatDateWithTime(new Date(event_1.data.insight.timestamp), {
        month: "short",
        year: "numeric"
      })].filter(Boolean).join(", ");
    };
    $0[6] = formatDateWithTime;
    $0[7] = t;
    $0[8] = t4;
  } else {
    t4 = $0[8];
  }
  const getEventLabel = t4;
  let t5;
  if ($0[9] !== formatDateWithTime || $0[10] !== t) {
    t5 = t6 => {
      const {
        event: event_2
      } = t6;
      return [t($_0 => $_0.visitPath[event_2.data.path]), event_2.data.userAgent && displayForUserAgent(event_2.data.userAgent), formatDateWithTime(new Date(event_2.data.timestamp), {
        month: "short",
        year: "numeric"
      })].filter(Boolean).join(", ");
    };
    $0[9] = formatDateWithTime;
    $0[10] = t;
    $0[11] = t5;
  } else {
    t5 = $0[11];
  }
  const getVisitLabel = t5;
  let t6;
  if ($0[12] !== select || $0[13] !== selectedId) {
    t6 = id => {
      const mapCard = mapCardRef.current;
      if (id !== selectedId && mapCard && getComputedStyle(mapCard).position !== "sticky") {
        mapCard.scrollIntoView({
          block: "nearest",
          behavior: getScrollBehavior()
        });
      }
      select(id, "list");
    };
    $0[12] = select;
    $0[13] = selectedId;
    $0[14] = t6;
  } else {
    t6 = $0[14];
  }
  const selectFromList = t6;
  let t7;
  if ($0[15] !== pickCount || $0[16] !== selectedFrom || $0[17] !== selectedRowId) {
    t7 = id_0 => id_0 === selectedRowId && selectedFrom === "map" ? pickCount : undefined;
    $0[15] = pickCount;
    $0[16] = selectedFrom;
    $0[17] = selectedRowId;
    $0[18] = t7;
  } else {
    t7 = $0[18];
  }
  const getMapPick = t7;
  let t8;
  if ($0[19] !== countryPins) {
    t8 = (id_1, isLocated) => isLocated ? id_1 : countryPins.find(t9 => {
      const {
        visitId
      } = t9;
      return visitId === id_1;
    })?.id;
    $0[19] = countryPins;
    $0[20] = t8;
  } else {
    t8 = $0[20];
  }
  const getVisitPinId = t8;
  let t9;
  if ($0[21] !== addresses || $0[22] !== countryPins || $0[23] !== getEventLabel || $0[24] !== getVisitLabel || $0[25] !== selectedId || $0[26] !== shownEvents || $0[27] !== t || $0[28] !== visits) {
    const pinned = [...shownEvents.flatMap(t10 => {
      const {
        id: id_2,
        event: event_3,
        coordinates
      } = t10;
      return coordinates ? [{
        id: id_2,
        coordinates,
        label: getEventLabel(event_3),
        getIcon: color => icoForUserAgent(event_3.event.data.insight.userAgent ?? "", color, "small")
      }] : [];
    }), ...visits.flatMap(t11 => {
      const {
        id: id_3,
        visit,
        coordinates: coordinates_0
      } = t11;
      return coordinates_0 ? [{
        id: id_3,
        coordinates: coordinates_0,
        label: getVisitLabel(visit),
        getIcon: color_0 => {
          const {
            userAgent
          } = visit.event.data;
          return userAgent ? icoForUserAgent(userAgent, color_0, "small") : <IcoUser16 color={color_0} />;
        }
      }] : [];
    }), ...countryPins.map(t12 => {
      const {
        id: id_4,
        country,
        coordinates: coordinates_1
      } = t12;
      return {
        id: id_4,
        coordinates: coordinates_1,
        label: `${t(_temp3)}: ${country}`,
        getIcon: _temp4
      };
    }), ...addresses.flatMap(t13 => {
      const {
        id: id_5,
        type,
        coordinates: coordinates_2
      } = t13;
      return coordinates_2 ? [{
        id: id_5,
        coordinates: coordinates_2,
        label: t($_2 => $_2.addressCard[type].headerText),
        getIcon: color_2 => type === AddressType.residential ? <IcoHome16 color={color_2} /> : <IcoBuilding16 color={color_2} />
      }] : [];
    })];
    const spots = new Map();
    pinned.forEach(pin => {
      const key = `${pin.coordinates.lat},${pin.coordinates.lng}`;
      spots.set(key, [...(spots.get(key) ?? []), pin]);
    });
    t9 = [...spots.entries()].map(t14 => {
      const [key_0, spot] = t14;
      const pin_0 = spot.find(t15 => {
        const {
          id: id_6
        } = t15;
        return id_6 === selectedId;
      }) ?? spot[0];
      return <MapMarker key={key_0} id={pin_0.id} lat={pin_0.coordinates.lat} lng={pin_0.coordinates.lng} label={spot.length > 1 ? t(_temp5, {
        label: pin_0.label,
        count: spot.length - 1
      }) : pin_0.label} getIcon={pin_0.getIcon} count={spot.length > 1 ? spot.length : undefined} isSelected={pin_0.id === selectedId} />;
    });
    $0[21] = addresses;
    $0[22] = countryPins;
    $0[23] = getEventLabel;
    $0[24] = getVisitLabel;
    $0[25] = selectedId;
    $0[26] = shownEvents;
    $0[27] = t;
    $0[28] = visits;
    $0[29] = t9;
  } else {
    t9 = $0[29];
  }
  const markers = t9;
  if (!devices.length && !signIns.length && !addresses.length) {
    let t10;
    if ($0[30] !== isFingerprint) {
      t10 = isFingerprint && <FingerprintAnnouncement />;
      $0[30] = isFingerprint;
      $0[31] = t10;
    } else {
      t10 = $0[31];
    }
    let t11;
    if ($0[32] !== t) {
      t11 = t(_temp6);
      $0[32] = t;
      $0[33] = t11;
    } else {
      t11 = $0[33];
    }
    let t12;
    if ($0[34] !== t11) {
      t12 = <Card.Root><Card.Empty>{t11}</Card.Empty></Card.Root>;
      $0[34] = t11;
      $0[35] = t12;
    } else {
      t12 = $0[35];
    }
    let t13;
    if ($0[36] !== t10 || $0[37] !== t12) {
      t13 = <div className="flex flex-col gap-3">{t10}{t12}</div>;
      $0[36] = t10;
      $0[37] = t12;
      $0[38] = t13;
    } else {
      t13 = $0[38];
    }
    return t13;
  }
  const hasMap = selectedCoords !== undefined;
  const t10 = hasMap && "@3xl:grid @3xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]";
  let t11;
  if ($0[39] !== t10) {
    t11 = cx("flex flex-col gap-3", t10);
    $0[39] = t10;
    $0[40] = t11;
  } else {
    t11 = $0[40];
  }
  const t12 = hasMap && "@3xl:max-h-[calc(100dvh-10rem-var(--onboardings-tabs-height,0px))] @3xl:overflow-y-auto";
  let t13;
  if ($0[41] !== t12) {
    t13 = cx("order-2 flex flex-col gap-3 @3xl:order-none", t12);
    $0[41] = t12;
    $0[42] = t13;
  } else {
    t13 = $0[42];
  }
  let t14;
  if ($0[43] !== isFingerprint) {
    t14 = isFingerprint && <FingerprintAnnouncement />;
    $0[43] = isFingerprint;
    $0[44] = t14;
  } else {
    t14 = $0[44];
  }
  let t15;
  if ($0[45] !== devices || $0[46] !== getMapPick || $0[47] !== getVisitPinId || $0[48] !== selectFromList || $0[49] !== selectedRowId || $0[50] !== t) {
    t15 = devices.length > 0 && <Group title={t(_temp7)} count={devices.length} isScrollable={devices.length > 2}><ul className="flex flex-col gap-0.5">{devices.map(entry => {
          const {
            id: id_7,
            coordinates: coordinates_3
          } = entry;
          if (!("visit" in entry)) {
            return <DeviceRow key={id_7} id={id_7} event={entry.event} isEmployeeOnly={entry.event.event.data.isSuperseded} isSelected={id_7 === selectedRowId} mapPick={getMapPick(id_7)} isLocatable={!!coordinates_3} onSelect={selectFromList} />;
          }
          const pinId = getVisitPinId(id_7, !!coordinates_3);
          return <VisitRow key={id_7} id={pinId ?? id_7} visit={entry.visit} isSelected={id_7 === selectedRowId} mapPick={getMapPick(id_7)} isLocatable={!!pinId} onSelect={selectFromList} />;
        })}</ul></Group>;
    $0[45] = devices;
    $0[46] = getMapPick;
    $0[47] = getVisitPinId;
    $0[48] = selectFromList;
    $0[49] = selectedRowId;
    $0[50] = t;
    $0[51] = t15;
  } else {
    t15 = $0[51];
  }
  let t16;
  if ($0[52] !== getMapPick || $0[53] !== isShowingAllSignIns || $0[54] !== isSignInsCollapsible || $0[55] !== selectFromList || $0[56] !== selectedRowId || $0[57] !== signInCount || $0[58] !== signIns || $0[59] !== t || $0[60] !== toggleAllSignIns) {
    t16 = signIns.length > 0 && <Group title={t(_temp8)} count={signInCount} isScrollable={isSignInsCollapsible ? isShowingAllSignIns : signIns.length > 2} footer={isSignInsCollapsible && <LinkButton className="mx-4 mt-1 mb-3 self-start" onClick={toggleAllSignIns}>{isShowingAllSignIns ? t(_temp9) : t(_temp10, {
        count: signInCount
      })}</LinkButton>}><ul className="flex flex-col gap-0.5">{signIns.map(t17 => {
          const {
            id: id_8,
            event: event_4,
            coordinates: coordinates_4
          } = t17;
          return <DeviceRow key={id_8} id={id_8} event={event_4} isEmployeeOnly={event_4.event.data.isSuperseded} isSelected={id_8 === selectedRowId} mapPick={getMapPick(id_8)} isLocatable={!!coordinates_4} onSelect={selectFromList} />;
        })}</ul></Group>;
    $0[52] = getMapPick;
    $0[53] = isShowingAllSignIns;
    $0[54] = isSignInsCollapsible;
    $0[55] = selectFromList;
    $0[56] = selectedRowId;
    $0[57] = signInCount;
    $0[58] = signIns;
    $0[59] = t;
    $0[60] = toggleAllSignIns;
    $0[61] = t16;
  } else {
    t16 = $0[61];
  }
  let t17;
  if ($0[62] !== addresses || $0[63] !== entity || $0[64] !== getMapPick || $0[65] !== selectFromList || $0[66] !== selectedRowId || $0[67] !== t) {
    t17 = addresses.length > 0 && <Group title={t(_temp11)} isScrollable={addresses.length > 2}><ul className="flex flex-col gap-0.5">{addresses.map(t18 => {
          const {
            id: id_9,
            type: type_0,
            isPending,
            coordinates: coordinates_5
          } = t18;
          return <AddressRow key={id_9} id={id_9} type={type_0} entity={entity} isPending={isPending} isSelected={id_9 === selectedRowId} mapPick={getMapPick(id_9)} isLocatable={!!coordinates_5} onSelect={selectFromList} />;
        })}</ul></Group>;
    $0[62] = addresses;
    $0[63] = entity;
    $0[64] = getMapPick;
    $0[65] = selectFromList;
    $0[66] = selectedRowId;
    $0[67] = t;
    $0[68] = t17;
  } else {
    t17 = $0[68];
  }
  let t18;
  if ($0[69] !== t13 || $0[70] !== t14 || $0[71] !== t15 || $0[72] !== t16 || $0[73] !== t17) {
    t18 = <div className={t13}>{t14}{t15}{t16}{t17}</div>;
    $0[69] = t13;
    $0[70] = t14;
    $0[71] = t15;
    $0[72] = t16;
    $0[73] = t17;
    $0[74] = t18;
  } else {
    t18 = $0[74];
  }
  let t19;
  if ($0[75] !== lines || $0[76] !== markers || $0[77] !== pickCount || $0[78] !== select || $0[79] !== selectedCoords) {
    t19 = selectedCoords && <div className="order-1 @3xl:order-none @3xl:min-h-64"><Card.Root ref={mapCardRef} className="flex h-80 scroll-mt-24 flex-col md:scroll-mt-[calc(var(--onboardings-strip-height,4rem)+var(--onboardings-tabs-height,0px)-0.75rem)] @3xl:h-[min(calc(100dvh-10rem-var(--onboardings-tabs-height,0px)),100%)] md:@3xl:sticky md:@3xl:top-[calc(var(--onboardings-strip-height,4rem)+var(--onboardings-tabs-height,0px)-0.75rem)]"><Card.Body className="min-h-0 flex-1 overflow-hidden"><MapComponent markers={markers} lines={lines} selectedCoords={selectedCoords} focusKey={pickCount} onSelect={id_10 => select(id_10, "map")} /></Card.Body></Card.Root></div>;
    $0[75] = lines;
    $0[76] = markers;
    $0[77] = pickCount;
    $0[78] = select;
    $0[79] = selectedCoords;
    $0[80] = t19;
  } else {
    t19 = $0[80];
  }
  let t20;
  if ($0[81] !== t11 || $0[82] !== t18 || $0[83] !== t19) {
    t20 = <div className={t11}>{t18}{t19}</div>;
    $0[81] = t11;
    $0[82] = t18;
    $0[83] = t19;
    $0[84] = t20;
  } else {
    t20 = $0[84];
  }
  return t20;
};
export default Content;
function _temp(event) {
  return isDeviceVisit(event) || isSuperseded(event);
}
function _temp2(event_0) {
  return !isSuperseded(event_0);
}
function _temp3($_1) {
  return $_1.deviceCountry;
}
function _temp4(color_1) {
  return <IcoGlobe16 color={color_1} />;
}
function _temp5($_3) {
  return $_3.pinWithMore;
}
function _temp6($_4) {
  return $_4.empty.title;
}
function _temp7($_5) {
  return $_5.groups.devices;
}
function _temp8($_6) {
  return $_6.groups.signIns;
}
function _temp9($_7) {
  return $_7.showLess;
}
function _temp10($_8) {
  return $_8.showAll;
}
function _temp11($_9) {
  return $_9.groups.addresses;
}